import { SectionShell } from "@/components/section-shell";
import { SagePeriod } from "@/components/sage-period";

const CAPABILITIES = [
  {
    index: "01",
    label: "Workload Categorization",
    description:
      "Build your asset inventory in hours instead of months of interviews.",
  },
  {
    index: "02",
    label: "Data Flows",
    description:
      "Know how data moves through your infrastructure across real workloads and resources.",
  },
  {
    index: "03",
    label: "Privacy Reviews",
    description:
      "Build your reviews on Truspecta's automated inventory, not error-prone surveys.",
  },
  {
    index: "04",
    label: "Privacy Findings",
    description:
      "Track and remediate privacy findings across your cloud infrastructure.",
  },
  {
    index: "05",
    label: "Drift Detection",
    description:
      "Take action when workloads drift from previously reviewed state.",
  },
  {
    index: "06",
    label: "Artifact Generation",
    description:
      "Automatically draft artifacts like RoPAs and DPIAs from evidence and privacy reviews.",
  },
] as const;

const CAPABILITY_COLUMNS = [
  CAPABILITIES.slice(0, 3),
  CAPABILITIES.slice(3),
] as const;

function CapabilityCard({ cap }: { cap: (typeof CAPABILITIES)[number] }) {
  return (
    <article className="grid grid-cols-[2.5rem_1fr] gap-3 rounded-lg border border-border bg-surface p-4 shadow-sm transition-shadow duration-200 hover:shadow-md sm:grid-cols-[3.25rem_1fr] sm:gap-4 sm:p-5">
      <span className="flex size-8 items-center justify-center rounded-md border border-primary/25 bg-primary-tint font-mono text-xs font-semibold text-primary sm:size-10">
        {cap.index}
      </span>
      <div>
        <h3 className="font-heading text-lg font-semibold tracking-tight text-foreground sm:text-xl">
          {cap.label}
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-muted sm:mt-2 sm:text-base">
          {cap.description}
        </p>
      </div>
    </article>
  );
}

export function EngineSection() {
  return (
    <SectionShell
      aria-labelledby="engine-heading"
      bg="secondary"
      maxWidth="6xl"
      padding="spacious"
    >
      <div className="mx-auto max-w-[820px] text-center">
        <h2
          id="engine-heading"
          className="font-heading text-[2.5rem] font-semibold leading-[1.05] tracking-[-0.03em] text-foreground sm:text-display-section"
        >
          Give privacy teams <span className="whitespace-nowrap">leverage<SagePeriod /></span>
        </h2>
        <p className="mt-4 text-base leading-relaxed text-foreground-muted">
          Replace manual discovery with a continuous view of workloads, reviews, findings, drift, and evidence.
        </p>
      </div>

      <div className="mt-12 grid gap-4 lg:grid-cols-2">
        {CAPABILITY_COLUMNS.map((column, columnIndex) => (
          <div key={columnIndex} className="grid gap-4">
            {column.map((cap) => (
              <CapabilityCard key={cap.label} cap={cap} />
            ))}
          </div>
        ))}
      </div>
    </SectionShell>
  );
}
